import {
  Banner,
  FlexBox,
  Modal,
  ModalFoot,
  ModalHead,
  Page,
  TabsWithContent,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import noop from 'lodash/noop';
import { PureComponent } from 'react';
import { connect } from 'react-redux';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { LanguageCustomizationList } from 'components/settings/components/sections/satisfaction/components/DixaMessengerCSAT/page/components/LanguageCustomizationList';
import { MessengersList } from 'components/settings/components/sections/satisfaction/components/DixaMessengerCSAT/page/components/MessengersList';
import { hasFeature } from 'components/shared/featureFlag';

import { GlobalCsatSwitch } from '../../common/GlobalCsatSwitch';
import { Csat } from '../../common/types';

import { DixaMessengerCSAT } from './types';

interface OwnProps {
  className?: string;
  existingMessengers: DixaMessengerCSAT.MessengerListSetting[];
  languages: DixaMessengerCSAT.Language[];
  onLanguageEditRequest: (language: DixaMessengerCSAT.Language) => void;
  onGlobalStatusChange: (change: Csat.CsatStatusChange) => void;
  onMessengerStatusChange: (
    change: DixaMessengerCSAT.MessengerStatusChange,
  ) => void;
  onLanguageStatusChange: (
    change: DixaMessengerCSAT.LanguageCustomizationStatusChange,
  ) => void;
  isLoadingMessengersList?: boolean;
  isMessengerCsatActive: boolean;
  isMessengerCsatSetup: boolean;
  defaultLanguage?: string;
  initiallyActiveTab: DixaMessengerCsatSettingTabs;
}

interface State {
  confirmationModal: {
    isOpen: boolean;
    context: DixaMessengerCSAT.MessengerStatusChange | null;
  };
}

interface StoreProps {
  userHasDixaMessengerCSAT: boolean;
}

export enum DixaMessengerCsatSettingTabs {
  Messengers = 'Messengers',
  LanguageLevelCustomizations = 'Language customizations',
}

type Props = OwnProps & StoreProps;

class DixaMessengerPageBase extends PureComponent<Props, State> {
  static defaultProps = {
    initiallyActiveTab: DixaMessengerCsatSettingTabs.Messengers,
  };

  constructor(props: Props) {
    super(props);
    this.state = { confirmationModal: { isOpen: false, context: null } };
  }

  @boundMethod
  handleMessengerStatusChange(
    change: DixaMessengerCSAT.MessengerStatusChange,
  ): void {
    if (this.statusHasChangedToDeActive(change)) {
      this.openConfirmationModal(change);
    } else {
      this.props.onMessengerStatusChange(change);
    }
  }

  @boundMethod
  handleConfirmedAccountStatusChange(): void {
    const { confirmationModal } = this.state;
    if (confirmationModal.context) {
      this.props.onMessengerStatusChange(confirmationModal.context);
    }
    this.closeConfirmationModal();
  }

  openConfirmationModal(
    context: DixaMessengerCSAT.MessengerStatusChange,
  ): void {
    this.setState({ confirmationModal: { isOpen: true, context: context } });
  }

  @boundMethod
  closeConfirmationModal(): void {
    this.setState({ confirmationModal: { isOpen: false, context: null } });
  }

  statusHasChangedToDeActive(
    change: DixaMessengerCSAT.MessengerStatusChange,
  ): boolean {
    return change.newStatus === false;
  }

  render() {
    const { userHasDixaMessengerCSAT } = this.props;
    const tabsDefinition = Object.values(DixaMessengerCsatSettingTabs).map(
      (tabName) => ({
        id: tabName,
        label: tabName,
      }),
    );

    const notAllowedToBeDisabledLanguages: string[] = [];

    if (this.props.defaultLanguage) {
      notAllowedToBeDisabledLanguages.push(this.props.defaultLanguage);
    }

    const onGlobalCsatToggle = userHasDixaMessengerCSAT
      ? this.props.onGlobalStatusChange
      : noop;

    const messengerListIsDisabled =
      !this.props.isMessengerCsatSetup || !userHasDixaMessengerCSAT;
    const languageCustomizationIsDisabled =
      !this.props.isMessengerCsatSetup || !userHasDixaMessengerCSAT;

    return (
      <>
        <Page.Section
          callout={
            <GlobalCsatSwitch
              channel="messenger"
              onChange={onGlobalCsatToggle}
              isActive={
                this.props.userHasDixaMessengerCSAT &&
                this.props.isMessengerCsatActive
              }
              disabled={!this.props.userHasDixaMessengerCSAT}
            />
          }
        >
          {!userHasDixaMessengerCSAT && (
            <Banner
              variant="announcement"
              headline="Upgrade plan to Advanced or Unlimited"
              description={
                <span>
                  Upgrade your plan to use a Customer satisfaction survey for
                  your messenger. Get in touch with your account manager for
                  more information.
                </span>
              }
            />
          )}

          <TabsWithContent
            initiallySelectedTabId={this.props.initiallyActiveTab}
            tabs={tabsDefinition}
          >
            <MessengersList
              messengers={this.props.existingMessengers}
              onStatusChange={this.handleMessengerStatusChange}
              isLoading={this.props.isLoadingMessengersList}
              disabled={messengerListIsDisabled}
            />

            {this.props.existingMessengers.length > 0 ? (
              <LanguageCustomizationList
                languages={this.props.languages}
                onEdit={this.props.onLanguageEditRequest}
                onLanguageStatusChange={this.props.onLanguageStatusChange}
                disabled={languageCustomizationIsDisabled}
                notAllowedToBeDisabledLanguages={
                  notAllowedToBeDisabledLanguages
                }
              />
            ) : (
              <FlexBox justifyContent="center" alignItems="center">
                No active messengers found!
              </FlexBox>
            )}
          </TabsWithContent>
        </Page.Section>

        <Modal
          isOpen={this.state.confirmationModal.isOpen}
          onClose={this.closeConfirmationModal}
        >
          <ModalHead title="Are you sure you want to disable CSAT for this messenger?">
            This will not trigger a CSAT when conversations are closed.
          </ModalHead>
          <ModalFoot
            confirmOnClick={this.handleConfirmedAccountStatusChange}
            cancelOnClick={this.closeConfirmationModal}
            confirmName="Disable"
          />
        </Modal>
      </>
    );
  }
}

export const DixaMessengerPage = connect<
  StoreProps,
  {},
  OwnProps,
  RootReducerStateT
>((state) => ({
  userHasDixaMessengerCSAT: hasFeature(state, 'csat-for-dixa-messenger'),
}))(DixaMessengerPageBase);
